import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Toggle from '@/design_system/Toggle';
import IconAfterSales from '@/icons/AfterSales.svg';
import { TEMPORARY_ID_PREFIX, useUpdateAction } from '@/models/article';
import { type ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionBrandResponsibilityMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  return (
    <Badge
      hasBorder
      color="neutral"
      borderRadius="soft"
      size="large"
      style={
        getEditedFields({ action, previousAction }).isBrandResponsibilityEdited
          ? { backgroundColor: 'var(--color-tertiary-100)' }
          : undefined
      }
    >
      <IconAfterSales />
      {(action ?? previousAction!).brandResponsibility ? (
        <Trans id="article.form.actions.card.brand-res.yes">Brand resp: Yes</Trans>
      ) : (
        <Trans id="article.form.actions.card.brand-res.no">Brand resp: No</Trans>
      )}
    </Badge>
  );
};

export const ActionBrandResponsibilityInput = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { t } = useLingui();
  const { article, request, view } = useArticleContext();

  const { mutate: updateAction } = useUpdateAction({
    articleId: article.id,
    requestId: request.id,
  });

  const isEditable =
    !!action &&
    view.services.actions.fields.brandResponsibility.editable &&
    !action?.id.startsWith(TEMPORARY_ID_PREFIX);

  return (
    <Toggle
      label={t({
        id: 'article.form.actions.table.column.brand-responsibility.label',
        message: 'Under brand responsibility',
      })}
      alignment="left"
      className={element('card-brand-responsibility', {
        edited: getEditedFields({ action, previousAction }).isBrandResponsibilityEdited,
      })}
      isSelected={!!(action ?? previousAction)?.brandResponsibility}
      onChange={(value) => {
        if (action) {
          updateAction({ actionId: action.id, body: { brandResponsibility: value } });
        }
      }}
      isDisabled={!isEditable}
    />
  );
};
